import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const KEEPS_SELECTION = '[role="menu"], [role="dialog"], .dropdown-menu, .track-context-dropdown, .modal, .modal-backdrop, .tooltip';
const OPEN_MENU = '.track-context-dropdown, .dropdown-menu.show, [role="menu"]';

// Finder-style row selection shared by every track-list surface (Library,
// Compatibility, Loudness, Organize, Cue Points). One model everywhere:
//   click = select · cmd/ctrl+click = toggle · shift+click = range from anchor
//   Cmd/Ctrl+A = select all · Escape = clear
//   ArrowUp/Down = move (single-select) · Shift+Arrow = extend range
//   Delete/Backspace = onDelete(selection, in visible order)
//
// Document-level key listeners are safe here because App.js mounts only the
// ACTIVE tab — an unmounted tab's listeners are gone with it.
//
// `keys`: ordered array of unique row keys as currently visible (MEMOIZE it in
//   the caller — a fresh array per render re-binds the listeners every render).
// `onDelete(orderedKeys)`: invoked on Delete/Backspace with the selection in
//   visible order. Omit to disable the delete keys.
// `onArrowFocus(index, key)`: scroll-into-view hook, called after arrow moves.
// `onPrimaryChange(key)`: fires when a SINGLE row becomes the selection via
//   plain click or plain arrow — for tabs where selection also drives a
//   detail view (Cue Points loads the clicked track's waveform).
// `rowSelector` (optional): the CSS selector of a row. When given, a click
//   anywhere that isn't a row clears the selection (Finder: click the empty
//   space or elsewhere). Leave it off where the highlight means more than a
//   selection (Cue Points: it marks the track the editor has open).
export default function useRowSelection({ keys, onDelete, onArrowFocus, onPrimaryChange, rowSelector }) {
    const [selected, setSelected] = useState(() => new Set());
    // Anchor = range start for shift-click/shift-arrow (last plain/cmd click).
    // Focus = the row the keyboard cursor is on (last click or arrow stop).
    const anchorRef = useRef(null);
    const focusRef = useRef(null);
    // Mirror for the document keydown listener so it never closes over a stale
    // selection Set.
    const selectedRef = useRef(selected);
    selectedRef.current = selected;

    // Rows can disappear under the selection (filter change, remove-from-list,
    // playlist switch) — prune silently so bulk actions never target ghosts.
    useEffect(() => {
        setSelected(prev => {
            if (!prev.size) return prev;
            const keySet = new Set(keys);
            const next = new Set();
            for (const k of prev) { if (keySet.has(k)) next.add(k); }
            return next.size === prev.size ? prev : next;
        });
        if (focusRef.current != null && focusRef.current >= keys.length) {
            focusRef.current = keys.length ? keys.length - 1 : null;
        }
        if (anchorRef.current != null && anchorRef.current >= keys.length) {
            anchorRef.current = keys.length ? keys.length - 1 : null;
        }
    }, [keys]);

    // Click outside the rows → nothing selected. Not for: the menus, modals and
    // tooltips a selection opens (their items act on it), the click that
    // dismisses an open menu, or a press on a scrollbar.
    useEffect(() => {
        if (!rowSelector) return undefined;
        const onDown = (e) => {
            if (e.button !== 0 || !selectedRef.current.size) return;
            const t = e.target;
            if (!(t instanceof Element)) return;
            if (t.closest(rowSelector) || t.closest(KEEPS_SELECTION)) return;
            if (document.querySelector(OPEN_MENU)) return;
            if (t.clientWidth && (e.offsetX > t.clientWidth || e.offsetY > t.clientHeight)) return;
            setSelected(new Set());
            anchorRef.current = null;
            focusRef.current = null;
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [rowSelector]);

    const clearSelection = useCallback(() => {
        setSelected(prev => (prev.size ? new Set() : prev));
        anchorRef.current = null;
        focusRef.current = null;
    }, []);

    // Retarget the selection to exactly one row (Finder right-click semantics).
    const selectOnly = useCallback((key, index) => {
        setSelected(prev => (prev.size === 1 && prev.has(key)) ? prev : new Set([key]));
        if (typeof index === 'number') { anchorRef.current = index; focusRef.current = index; }
    }, []);

    const handleRowClick = useCallback((index, key, e) => {
        if (!key) return;
        // A click that ends a text drag-selection (Artist/Title cells) must not
        // also retarget the row selection.
        const textSel = window.getSelection && window.getSelection();
        if (textSel && !textSel.isCollapsed && String(textSel).length > 0) return;
        if (e.shiftKey && anchorRef.current != null) {
            const anchor = anchorRef.current;
            const [from, to] = anchor < index ? [anchor, index] : [index, anchor];
            const range = keys.slice(from, to + 1);
            setSelected(prev => {
                const next = (e.metaKey || e.ctrlKey) ? new Set(prev) : new Set();
                range.forEach(k => next.add(k));
                return next;
            });
            focusRef.current = index;
        } else if (e.metaKey || e.ctrlKey) {
            setSelected(prev => {
                const next = new Set(prev);
                if (next.has(key)) next.delete(key); else next.add(key);
                return next;
            });
            anchorRef.current = index;
            focusRef.current = index;
        } else {
            setSelected(prev => (prev.size === 1 && prev.has(key)) ? prev : new Set([key]));
            anchorRef.current = index;
            focusRef.current = index;
            if (onPrimaryChange) onPrimaryChange(key);
        }
    }, [keys, onPrimaryChange]);

    useEffect(() => {
        const onKeyDown = (e) => {
            // Another widget (range-slider thumb, open dropdown) already
            // consumed the key — don't fight it for the arrow/delete keys.
            if (e.defaultPrevented) return;
            const t = e.target;
            const tag = (t?.tagName || '').toLowerCase();
            if (tag === 'input' || tag === 'textarea' || t?.isContentEditable) return;
            if (t?.closest && t.closest('[role="slider"], [role="menu"], .dropdown-menu')) return;
            if (e.key === 'Escape') {
                setSelected(prev => (prev.size ? new Set() : prev));
                return;
            }
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a') {
                if (!keys.length) return;
                e.preventDefault();
                setSelected(new Set(keys));
                return;
            }
            if (e.key === 'Delete' || e.key === 'Backspace') {
                if (!onDelete || selectedRef.current.size === 0) return;
                e.preventDefault();
                onDelete(keys.filter(k => selectedRef.current.has(k)));
                return;
            }
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                if (!keys.length) return;
                // Leave cmd/alt+arrow combos (word/line navigation, app
                // shortcuts) alone.
                if (e.metaKey || e.ctrlKey || e.altKey) return;
                e.preventDefault();
                const dir = e.key === 'ArrowDown' ? 1 : -1;
                let next;
                if (focusRef.current == null || selectedRef.current.size === 0) {
                    // No keyboard cursor yet: Down enters from the top, Up from
                    // the bottom.
                    next = dir === 1 ? 0 : keys.length - 1;
                } else {
                    next = Math.min(keys.length - 1, Math.max(0, focusRef.current + dir));
                }
                if (e.shiftKey && anchorRef.current != null && selectedRef.current.size > 0) {
                    const anchor = anchorRef.current;
                    const [from, to] = anchor < next ? [anchor, next] : [next, anchor];
                    setSelected(new Set(keys.slice(from, to + 1)));
                    focusRef.current = next;
                } else {
                    setSelected(new Set([keys[next]]));
                    anchorRef.current = next;
                    focusRef.current = next;
                    if (onPrimaryChange) onPrimaryChange(keys[next]);
                }
                if (onArrowFocus) onArrowFocus(next, keys[next]);
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [keys, onDelete, onArrowFocus, onPrimaryChange]);

    // Selection in visible order — what bulk actions should operate on.
    const orderedSelected = useMemo(
        () => (selected.size ? keys.filter(k => selected.has(k)) : []),
        [keys, selected]
    );

    return { selected, orderedSelected, handleRowClick, clearSelection, selectOnly };
}
